import {
  abbreviateId,
  CareOrder,
  getQuoteLegQuantity,
  isCareOrder,
  isKeyIn,
  isOrder,
  isQuote,
  Order,
  Quote,
  QuoteStatusEnum,
  toBig,
  type AssertNoProperty,
  type Expect,
  type QuoteLegRepresentative,
  type QuoteMarket,
  type TreeRow,
  type UpdateActionEnum,
} from '@talos/kyoko';
import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';
import { compact } from 'lodash-es';

export type OrderWithParentOrderID = RequireKeys<Order, 'ParentOrderID'>;
export type QuoteWithParentRFQID = RequireKeys<Quote, 'ParentRFQID'>;

export type CareOrderBlotterEntity = {
  CareOrderID: string;
  rowID: string;
  id: string;
  dataPath: string[];
  RowType: string;
  tradedSideOrSide: string | undefined;
  /** Attached by the blotter pipeline when emitting an AG Grid row removal. */
  UpdateAction?: UpdateActionEnum;
} & (CareOrderRow | OrderRow | QuoteRow | QuoteRowWithOrder | LegRow);

export type CareOrderChildRow = OrderRow | QuoteRow;

export function createCareOrderBlotterEntity(data: CareOrder, childRows?: CareOrderChildRow[]): CareOrderRow;
export function createCareOrderBlotterEntity(data: OrderWithParentOrderID): OrderRow;
export function createCareOrderBlotterEntity(data: QuoteWithParentRFQID): QuoteRow;
export function createCareOrderBlotterEntity<T>(
  data: T,
  childRows?: CareOrderChildRow[]
): CareOrderRow | OrderRow | QuoteRow {
  if (isOrder(data) && data.ParentOrderID) {
    return new OrderRow(data as OrderWithParentOrderID);
  }
  if (isQuote(data) && data.ParentRFQID) {
    return new QuoteRow(data as QuoteWithParentRFQID);
  }
  if (isCareOrder(data)) {
    return new CareOrderRow(data, childRows);
  }
  throw new Error('unknown data type');
}

export type QuoteRowWithOrder = QuoteRow & OrderWithParentOrderID & QuoteMarket;
export class QuoteRow extends Quote implements TreeRow {
  override ParentRFQID: string;
  get RowType() {
    return 'RFQ';
  }
  get id() {
    return this.RFQID;
  }

  get netNotional(): string | undefined {
    const qtyBig = toBig(this.TradedQty);
    const fixingPriceBig = toBig(this.FixingDetails?.Fixing);
    if (!qtyBig || !fixingPriceBig) {
      return undefined;
    }

    return qtyBig.times(fixingPriceBig).toFixed();
  }

  get fees(): string | undefined {
    const filledNotionalBig = toBig(this.TradedAmt);
    const netNotionalBig = toBig(this.netNotional);
    if (!filledNotionalBig || !netNotionalBig) {
      return undefined;
    }

    if (this.TradedSide == null) {
      return undefined;
    }

    return this.TradedSide === 'Buy'
      ? filledNotionalBig.minus(netNotionalBig).toFixed()
      : netNotionalBig.minus(filledNotionalBig).toFixed();
  }

  get filledQty() {
    return this.TradedQty;
  }

  get filledAmt() {
    return this.TradedAmt;
  }

  groupColumnValueGetter(params: ValueGetterParams) {
    return this.id;
  }

  groupColumnValueFormatter(params: ValueFormatterParams) {
    return this.id;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams) {
    return this.id;
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<QuoteRow, string>) {
    return params.value ? abbreviateId(params.value) : '';
  }

  constructor(data: QuoteWithParentRFQID) {
    super(data);
    this.ParentRFQID = data.ParentRFQID;
  }

  get rowID() {
    return this.id;
  }

  get CareOrderID() {
    return this.ParentRFQID;
  }

  get dataPath(): string[] {
    return [this.ParentRFQID, this.id];
  }

  /**
   * TODO remove this proxy entirely, it is too clever.
   * The QuoteRow is a special snowflake, since it's a combination of an RFQ and an order.
   *
   * For the properties, we do the following prioritization:
   * 1. Use property from the RFQ
   * 2. Use property from the order
   */
  enrichWith(order: OrderWithParentOrderID): QuoteRowWithOrder {
    const next = new QuoteRow(this);
    return new Proxy(next, {
      get(quote, property) {
        if (isKeyIn(property, quote)) {
          return quote[property];
        }
        return order[property as keyof typeof order];
      },
    }) as QuoteRowWithOrder;
  }
}

export class OrderRow extends Order implements TreeRow {
  override ParentOrderID: string;
  get RowType() {
    return 'ORDER';
  }
  get id() {
    return this.OrderID;
  }

  get filledQty() {
    return this.CumQty;
  }

  get filledAmt() {
    return this.CumAmt;
  }

  groupColumnValueGetter(params: ValueGetterParams) {
    return this.id;
  }

  groupColumnValueFormatter() {
    return this.id;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<OrderRow, string>) {
    return params.value ? abbreviateId(params.value) : '';
  }

  constructor(data: OrderWithParentOrderID) {
    super(data);
    this.ParentOrderID = data.ParentOrderID;
  }

  get rowID() {
    return this.id;
  }

  get CareOrderID() {
    return this.ParentOrderID;
  }

  get dataPath(): string[] {
    return [this.ParentOrderID, this.id];
  }

  /** The CareOrderBlotter looks at .tradedSideOrSide to render the side, so adapt OrderRow for that purpose. */
  get tradedSideOrSide() {
    return this.Side;
  }
}

export class LegRow implements TreeRow {
  ParentRFQID: string;
  RFQID: string;
  QuoteID: string;
  QuoteStatus: Quote['QuoteStatus'];
  SubmitTime: Quote['SubmitTime'];
  Side: Quote['Side'];
  TradedSide: Quote['TradedSide'];
  selectedMarkets: string[];
  PricingMode: Quote['PricingMode'];

  Symbol: QuoteLegRepresentative['Symbol'];
  Currency: QuoteLegRepresentative['Currency'];
  AmountCurrency: QuoteLegRepresentative['AmountCurrency'];
  OrderQty: QuoteLegRepresentative['OrderQty'];
  TradedQty: QuoteLegRepresentative['TradedQty'];
  TradedAmt: QuoteLegRepresentative['TradedAmt'];
  TradedPx: QuoteLegRepresentative['TradedPx'];
  TransactTime: QuoteLegRepresentative['TransactTime'];
  PricingReference: QuoteLegRepresentative['PricingReference'];
  FixingDetails: QuoteLegRepresentative['FixingDetails'];
  fixingIndices: string[];
  quoteMarketsByName: Record<string, { BidPx?: string; OfferPx?: string }>;
  tradedMarkets: string[];
  tradedMarketAccounts: string[];

  constructor(params: { quote: QuoteRow; representativeLeg: QuoteLegRepresentative }) {
    const { quote, representativeLeg } = params;
    this.ParentRFQID = quote.ParentRFQID;
    this.RFQID = quote.RFQID;
    this.QuoteID = quote.QuoteID;
    this.QuoteStatus = quote.QuoteStatus;
    this.SubmitTime = quote.SubmitTime;
    this.Side = quote.Side;
    this.PricingMode = quote.PricingMode;

    this.Symbol = representativeLeg.Symbol;
    this.Currency = representativeLeg.Currency;
    this.AmountCurrency = representativeLeg.AmountCurrency;
    this.OrderQty = getQuoteLegQuantity(quote.Side, representativeLeg);
    this.TradedQty = representativeLeg.TradedQty;
    this.TradedAmt = representativeLeg.TradedAmt;
    this.TradedPx = representativeLeg.TradedPx;
    this.TradedSide = representativeLeg.TradedSide;
    this.TransactTime = representativeLeg.TransactTime;
    this.PricingReference = representativeLeg.PricingReference;
    this.FixingDetails = representativeLeg.FixingDetails;
    this.fixingIndices = compact([this.FixingDetails?.Index]);
    this.quoteMarketsByName = Object.fromEntries(
      (quote.Legs ?? [])
        .filter(leg => leg.Symbol === representativeLeg.Symbol)
        .map(leg => [leg.Market, { BidPx: leg.BidPx, OfferPx: leg.OfferPx }])
    );
    this.selectedMarkets = Array.from(
      new Set((quote.Legs ?? []).filter(leg => leg.Symbol === representativeLeg.Symbol).map(leg => leg.Market))
    );
    this.tradedMarkets = compact([representativeLeg.Market]);
    this.tradedMarketAccounts = compact([representativeLeg.MarketAccount]);
  }

  get filledQty() {
    return this.TradedQty;
  }

  get filledAmt() {
    return this.TradedAmt;
  }

  get id() {
    return `${this.RFQID}-${this.Symbol}`;
  }

  get RowType() {
    return 'LEG';
  }

  get rowID() {
    return this.id;
  }

  get CareOrderID() {
    return this.ParentRFQID;
  }

  get fixingDate() {
    return this.FixingDetails?.FixingDate;
  }

  get dataPath(): string[] {
    return [this.ParentRFQID, this.RFQID, this.Symbol];
  }

  groupColumnValueGetter(params: ValueGetterParams) {
    return this.RFQID;
  }

  groupColumnValueFormatter(params: ValueFormatterParams) {
    return abbreviateId(this.RFQID);
  }

  groupColumnFilterValueGetter(params: ValueGetterParams) {
    return this.RFQID;
  }

  get tradedSideOrSide() {
    return this.TradedSide ?? this.Side;
  }
}

export class CareOrderRow extends CareOrder implements TreeRow {
  hasPendingFixingQty: boolean;
  doneQty: string;
  pendingFixingQty: string;

  constructor(data: CareOrder, childRows: CareOrderChildRow[] = []) {
    super(data);
    const { doneQty, pendingFixingQty, hasPendingFixingQty } = getCareOrderFilledQtySplit(data, childRows);
    this.hasPendingFixingQty = hasPendingFixingQty;
    this.doneQty = doneQty;
    this.pendingFixingQty = pendingFixingQty;
  }

  get RowType() {
    return this.isNetted ? 'NETTED' : 'CARE ORDER';
  }

  get id() {
    return this.OrderID;
  }

  get filledQty() {
    return this.CumQty;
  }

  get filledAmt() {
    return this.CumAmt;
  }

  groupColumnValueGetter(params: ValueGetterParams) {
    return this.id;
  }

  groupColumnValueFormatter() {
    return this.id;
  }

  groupColumnFilterValueGetter(params: ValueGetterParams) {
    return this.groupColumnValueGetter(params);
  }

  groupColumnInnerCellRenderer(params: ICellRendererParams<CareOrderRow, string>) {
    return params.value ? abbreviateId(params.value) : '';
  }

  get rowID() {
    return this.id;
  }

  get CareOrderID() {
    return this.OrderID;
  }

  get dataPath(): string[] {
    return [this.id];
  }

  /** The CareOrderBlotter looks at .tradedSideOrSide to render the side, so adapt CareOrderRow for that purpose. */
  get tradedSideOrSide() {
    return this.Side;
  }
}

export function isCareOrderRow(row: unknown): row is CareOrderRow {
  return row instanceof CareOrderRow;
}

export function isQuoteRow(row: unknown): row is QuoteRow | QuoteRowWithOrder {
  return row instanceof QuoteRow;
}

export function isLegRow(row: unknown): row is LegRow {
  return row instanceof LegRow;
}

// If we ever add OrderID to Quote the below assertion breaks so testing for that here
type _NoOrderIDInQuote = Expect<AssertNoProperty<Quote, 'OrderID'>>;

export function isQuoteRowWithOrder(row: unknown): row is QuoteRowWithOrder {
  return row instanceof QuoteRow && (row as any)['OrderID'] != null;
}

export function isOrderRow(row: unknown): row is OrderWithParentOrderID {
  return row instanceof OrderRow;
}

export const CARE_ORDER_BLOTTER_PREFIX = 'care-order-blotter';
export const CARE_ORDER_BLOTTER_PORTAL_ID = 'care-order-blotter-portal';

/**
 * Care orders know total traded quantity, but not how much of that quantity is still pending fixing.
 * Child rows provide that split: pending-fix quotes contribute TradedQty when available, falling back to OrderQty,
 * and the remaining care-order CumQty is treated as fixing-applied.
 */
function getCareOrderFilledQtySplit(careOrder: CareOrder, childRows: CareOrderChildRow[]) {
  const pendingFixingQty = childRows.reduce((total, childRow) => {
    if (childRow instanceof QuoteRow && childRow.QuoteStatus === QuoteStatusEnum.PendingFix) {
      return total.plus(toBig(childRow.TradedQty) ?? toBig(childRow.OrderQty) ?? 0);
    }

    return total;
  }, Big(0));
  const doneQty = toBig(careOrder.CumQty)?.minus(pendingFixingQty) ?? Big(0);

  return {
    hasPendingFixingQty: pendingFixingQty.gt(0),
    doneQty: doneQty.toFixed(),
    pendingFixingQty: pendingFixingQty.toFixed(),
  };
}
